/**
 * Statements — the monthly scorecard statements' admin page (docs/plans/scorecard-statements.md,
 * PR 5): who is emailed, the monthly close check's notices, and approving held statements.
 * What `npm run statements:send -- --set-rollout`, `--set-notices` and `--approve` did on
 * the command line, for the full-access role (GET /api/dev `statements`; Yehuda,
 * 2026-10-27). Off while viewing as someone.
 *
 * Nothing here sends anything. The settings are read by the next send; an approved
 * statement goes out with it. The API (api/shared/statementAdmin.ts) checks every request.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { apiFetch } from '@/auth/apiFetch'
import { TOKEN_HEADER } from '@/data/client'
import { useDataset } from '@/data/DatasetProvider'
import { viewingAs } from '@/data/viewAs'
import { useDevMode } from '@/ui/devMode'
import { SearchPick } from '@/components/SearchPick'
import { monthYear } from '@/format'
import { NOTHING, type RolloutSettings } from '@/statements/send/rollout'
import { NO_NOTICES, noticesSentence, type NoticeSettings } from '../../api/shared/notices'
import { pilotCandidates, pilotPeople, reasonText, reviewRows, rolloutSentence, sameSettings, sendState, sendsSummary } from '@/statements/admin'

/** What each statement is, to the person approving it (MyStatements' labels are the recipient's). */
const KIND_LABEL: Record<'employee' | 'team' | 'department', string> = {
  employee: 'Employee scorecard',
  team: 'Team scorecard',
  department: 'Department scorecard',
}

interface Saved {
  by: string
  at: string
}

interface Run {
  runId: string
  mode: 'dry-run' | 'final'
  createdAt: string
  createdBy: string
  statements: number
  held: number
}

interface Held {
  id: number
  kind: 'employee' | 'team' | 'department'
  employee: string
  holdReasons: string | null
  status: string
  approvedBy: string | null
  approvedAt: string | null
}

/** One person on the run's review list (the API reads it from the run's summary.json). */
interface ReviewPerson {
  employee: string
  reasons: string[]
}

type MonthData = { runs: Run[]; run: string | null; statements: Held[]; review: ReviewPerson[] | null }

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })

/** The month statements are made for: last month. */
function lastMonth(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function useApi() {
  const { token } = useAuth()
  return useCallback(
    async <T,>(path: string, init?: { method?: string; body?: unknown }): Promise<T> => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (token) headers[TOKEN_HEADER] = token
      if (init?.body !== undefined) headers['Content-Type'] = 'application/json'
      const r = await apiFetch(`/api/statement-admin/${path}`, {
        method: init?.method ?? 'GET',
        headers,
        body: init?.body === undefined ? undefined : JSON.stringify(init.body),
      })
      const body = (await r.json().catch(() => ({}))) as T & { error?: string }
      if (!r.ok) throw new Error(body.error ?? `The server answered ${r.status}.`)
      return body
    },
    [token],
  )
}

function Notice({ what, why }: { what: string; why?: string }) {
  return (
    <div className="notice" style={{ marginTop: 18 }}>
      <p className="notice-what">{what}</p>
      {why && <p className="notice-why">{why}</p>}
    </div>
  )
}

export function StatementsAdmin() {
  const dev = useDevMode()
  if (viewingAs()) {
    return (
      <>
        <h1 className="page-title">Statements</h1>
        <Notice
          what="Statements admin is off while viewing as someone."
          why="Viewing as is read-only. Exit it to change who is emailed."
        />
      </>
    )
  }
  if (dev.features === null) return <span className="eyebrow">Checking your access…</span>
  if (!dev.features.statements) {
    return (
      <>
        <h1 className="page-title">Statements</h1>
        <Notice what="Statements admin needs full access." />
      </>
    )
  }
  return (
    // rules-page: the admin pages' width and button states (styles/rules.css).
    <div className="rules-page">
      <h1 className="page-title">Statements</h1>
      <p className="body-note" style={{ maxWidth: 760 }}>
        The monthly scorecard statements: who is emailed, and which held statements may go. Nothing is sent from this page; the
        next send reads what is saved here.
      </p>
      <Rollout />
      <Notices />
      <HeldStatements />
    </div>
  )
}

/* ------------------------------------------------------------------ who is emailed */

const radio: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'baseline', margin: '6px 0' }

function Rollout() {
  const api = useApi()
  const { dataset } = useDataset()
  const [saved, setSaved] = useState<{ settings: RolloutSettings; saved: Saved | null } | null>(null)
  const [draft, setDraft] = useState<RolloutSettings>(NOTHING)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [everyone, setEveryone] = useState(false)

  useEffect(() => {
    let cancelled = false
    api<{ settings: RolloutSettings; saved: Saved | null }>('rollout')
      .then((b) => {
        if (cancelled) return
        setSaved(b)
        setDraft(b.settings)
      })
      .catch((e: Error) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [api])

  const candidates = useMemo(() => pilotCandidates(dataset?.employees ?? []), [dataset])
  const people = useMemo(() => pilotPeople(draft.pilot, candidates), [draft.pilot, candidates])
  const picked = useMemo(() => new Set(draft.pilot.map((k) => k.toLowerCase())), [draft.pilot])
  const options = useMemo(
    () =>
      candidates
        .filter((c) => (everyone || c.lead) && !picked.has(c.fccKey.toLowerCase()))
        .map((c) => ({ value: c.fccKey, label: c.name, detail: [c.jobTitle, c.department].filter(Boolean).join(' · ') })),
    [candidates, everyone, picked],
  )
  const departments = useMemo(
    () => [...new Set((dataset?.employees ?? []).map((e) => e.department).filter(Boolean))].sort(),
    [dataset],
  )

  if (error && !saved) return <Notice what="Who is emailed could not be loaded." why={error} />
  if (!saved) return <span className="eyebrow">Loading who is emailed…</span>

  const dirty = !sameSettings(draft, saved.settings)
  const set = (patch: Partial<RolloutSettings>) => setDraft((d) => ({ ...d, ...patch }))
  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const b = await api<{ settings: RolloutSettings; saved: Saved | null }>('rollout', {
        method: 'PUT',
        body: { settings: draft },
      })
      setSaved(b)
      setDraft(b.settings)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card" aria-labelledby="rollout-h" style={{ marginTop: 18, maxWidth: 860 }}>
      <h2 id="rollout-h" className="section-heading" style={{ marginTop: 0 }}>
        Who is emailed
      </h2>
      <p className="body-note" style={{ marginTop: 0 }}>
        {rolloutSentence(saved.settings)}{' '}
        {saved.saved ? `Saved by ${saved.saved.by}, ${when(saved.saved.at)}.` : 'Nothing saved yet.'}
      </p>

      <fieldset style={{ border: 0, padding: 0, margin: '12px 0' }}>
        <legend className="eyebrow">Phase</legend>
        <label style={radio}>
          <input type="radio" name="phase" checked={draft.pilotOnly} onChange={() => set({ pilotOnly: true })} />
          <span>Pilot: only the people listed below</span>
        </label>
        <label style={radio}>
          <input type="radio" name="phase" checked={!draft.pilotOnly} onChange={() => set({ pilotOnly: false })} />
          <span>Every supervisor and manager, and employees as set below</span>
        </label>
      </fieldset>

      {draft.pilotOnly ? (
        <div>
          <p className="eyebrow" style={{ marginBottom: 6 }}>
            Pilot list ({people.length})
          </p>
          {people.length === 0 ? (
            <p className="body-note">Nobody yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="dt" style={{ width: '100%', marginBottom: 10 }}>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.fccKey}>
                      {'missing' in p ? (
                        <td colSpan={3}>
                          <span className="mono">{p.fccKey}</span> <span className="eyebrow">not in this data</span>
                        </td>
                      ) : (
                        <>
                          <td>{p.name}</td>
                          <td>{p.jobTitle}</td>
                          <td>{p.department}</td>
                        </>
                      )}
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn"
                          aria-label={`Remove ${'missing' in p ? p.fccKey : p.name}`}
                          onClick={() => set({ pilot: draft.pilot.filter((k) => k.toLowerCase() !== p.fccKey.toLowerCase()) })}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 260, flex: '1 1 260px', maxWidth: 420 }}>
              <SearchPick
                label="Add someone"
                value=""
                placeholder={everyone ? 'Search everyone' : 'Search supervisors and managers'}
                options={options}
                onChange={(k) => k && set({ pilot: [...draft.pilot, k] })}
              />
            </div>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-table)' }}>
              <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} />
              List everyone, not only supervisors and managers
            </label>
          </div>
        </div>
      ) : (
        <fieldset style={{ border: 0, padding: 0, margin: '12px 0' }}>
          <legend className="eyebrow">Employees' own statements</legend>
          <label style={radio}>
            <input type="radio" name="emp" checked={draft.employees === 'off'} onChange={() => set({ employees: 'off' })} />
            <span>None</span>
          </label>
          <label style={radio}>
            <input
              type="radio"
              name="emp"
              checked={draft.employees === 'departments'}
              onChange={() => set({ employees: 'departments' })}
            />
            <span>Employees in these departments</span>
          </label>
          {draft.employees === 'departments' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', margin: '4px 0 8px 24px' }}>
              {departments.map((d) => (
                <label key={d} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-table)' }}>
                  <input
                    type="checkbox"
                    checked={draft.departments.some((x) => x.toLowerCase() === d.toLowerCase())}
                    onChange={(e) =>
                      set({
                        departments: e.target.checked
                          ? [...draft.departments, d]
                          : draft.departments.filter((x) => x.toLowerCase() !== d.toLowerCase()),
                      })
                    }
                  />
                  {d}
                </label>
              ))}
            </div>
          )}
          <label style={radio}>
            <input type="radio" name="emp" checked={draft.employees === 'all'} onChange={() => set({ employees: 'all' })} />
            <span>Every employee</span>
          </label>
        </fieldset>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn rules-primary" disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn" disabled={!dirty || busy} onClick={() => setDraft(saved.settings)}>
          Undo changes
        </button>
        {dirty && <span className="body-note">Not saved. After saving: {rolloutSentence(draft)}</span>}
      </div>
      {error && (
        <p className="body-note" role="alert" style={{ margin: '8px 0 0', color: 'var(--crit-ink)' }}>
          {error}
        </p>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ close-check notices */

const sameNotices = (a: NoticeSettings, b: NoticeSettings) =>
  a.monthly === b.monthly && a.owner === b.owner && a.alertFromDay === b.alertFromDay && [...a.notify].sort().join('\n') === [...b.notify].sort().join('\n')

const field: React.CSSProperties = { display: 'grid', gap: 4, maxWidth: 420 }

function Notices() {
  const api = useApi()
  const [saved, setSaved] = useState<{ settings: NoticeSettings; saved: Saved | null } | null>(null)
  const [draft, setDraft] = useState<NoticeSettings>(NO_NOTICES)
  const [notifyText, setNotifyText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    api<{ settings: NoticeSettings; saved: Saved | null }>('notices')
      .then((b) => {
        if (cancelled) return
        setSaved(b)
        setDraft(b.settings)
        setNotifyText(b.settings.notify.join('\n'))
      })
      .catch((e: Error) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [api])

  if (error && !saved) return <Notice what="The close-check notices could not be loaded." why={error} />
  if (!saved) return <span className="eyebrow">Loading the close-check notices…</span>

  const notify = notifyText
    .split(/[\n,;]/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
  const current: NoticeSettings = { ...draft, owner: draft.owner?.trim().toLowerCase() || null, notify: [...new Set(notify)] }
  const dirty = !sameNotices(current, saved.settings)
  const set = (patch: Partial<NoticeSettings>) => setDraft((d) => ({ ...d, ...patch }))
  const reset = () => {
    setDraft(saved.settings)
    setNotifyText(saved.settings.notify.join('\n'))
  }
  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const b = await api<{ settings: NoticeSettings; saved: Saved | null }>('notices', { method: 'PUT', body: { settings: current } })
      setSaved(b)
      setDraft(b.settings)
      setNotifyText(b.settings.notify.join('\n'))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card" aria-labelledby="notices-h" style={{ marginTop: 18, maxWidth: 860 }}>
      <h2 id="notices-h" className="section-heading" style={{ marginTop: 0 }}>
        Month-end close check
      </h2>
      <p className="body-note" style={{ marginTop: 0 }}>
        {noticesSentence(saved.settings)} {saved.saved ? `Saved by ${saved.saved.by}, ${when(saved.saved.at)}.` : 'Nothing saved yet.'}
      </p>
      <p className="body-note">
        Each day from the 1st to the 10th, the Function App checks whether last month has closed (every claim in, payroll
        loaded). Nothing is generated or sent while it has not. Who hears about it is set here; it takes effect at the
        next day's check.
      </p>

      <fieldset style={{ border: 0, padding: 0, margin: '12px 0' }}>
        <legend className="eyebrow">What the check does</legend>
        <label style={radio}>
          <input type="radio" name="monthly" checked={draft.monthly === 'off'} onChange={() => set({ monthly: 'off' })} />
          <span>Off</span>
        </label>
        <label style={radio}>
          <input type="radio" name="monthly" checked={draft.monthly === 'check'} onChange={() => set({ monthly: 'check' })} />
          <span>Check and report only (the pilot: statements are generated by hand)</span>
        </label>
        <label style={radio}>
          <input type="radio" name="monthly" checked={draft.monthly === 'generate'} onChange={() => set({ monthly: 'generate' })} />
          <span>Check, and generate the month's statements once it closes</span>
        </label>
      </fieldset>

      <div style={{ display: 'grid', gap: 12 }}>
        <label style={field}>
          <span className="eyebrow">Program owner: alerted each day the month is still not closed</span>
          <input type="email" value={draft.owner ?? ''} placeholder="name@fcc-corp.com" onChange={(e) => set({ owner: e.target.value })} />
        </label>
        <label style={field}>
          <span className="eyebrow">Alert from day</span>
          <select value={draft.alertFromDay} onChange={(e) => set({ alertFromDay: Number(e.target.value) })} style={{ maxWidth: 120 }}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <span className="body-note" style={{ margin: 0 }}>
            The days before payroll normally lands stay quiet.
          </span>
        </label>
        <label style={field}>
          <span className="eyebrow">Notify on every outcome (one address per line)</span>
          <textarea rows={3} value={notifyText} placeholder="name@fcc-corp.com" onChange={(e) => setNotifyText(e.target.value)} />
          <span className="body-note" style={{ margin: 0 }}>
            Closed and queued, closed and not queued, not closed with whether the alert went out, or an error.
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn rules-primary" disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn" disabled={!dirty || busy} onClick={reset}>
          Undo changes
        </button>
        {dirty && <span className="body-note">Not saved. After saving: {noticesSentence(current)}</span>}
      </div>
      {error && (
        <p className="body-note" role="alert" style={{ margin: '8px 0 0', color: 'var(--crit-ink)' }}>
          {error}
        </p>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ held statements */

function HeldStatements() {
  const api = useApi()
  const [month, setMonth] = useState(lastMonth)
  const [run, setRun] = useState<string | null>(null)
  const [data, setData] = useState<MonthData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ticked, setTicked] = useState<Set<number>>(new Set())
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return
    let cancelled = false
    setError(null)
    setData(null)
    setTicked(new Set())
    api<MonthData>(`month?month=${month}${run ? `&run=${run}` : ''}`)
      .then((b) => !cancelled && setData(b))
      .catch((e: Error) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [api, month, run])

  const waiting = data?.statements.filter((s) => s.status === 'held') ?? []
  const approved = data?.statements.filter((s) => s.approvedBy) ?? []
  const current = data?.runs.find((r) => r.runId === data.run)
  const review = data?.review ? reviewRows(data.review, data.statements) : null

  const approve = async () => {
    if (!data?.run || ticked.size === 0) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const b = await api<{ approved: number; asked: number; statements: Held[] }>('approve', {
        method: 'POST',
        body: { runId: data.run, ids: [...ticked] },
      })
      setData({ ...data, statements: b.statements })
      setTicked(new Set())
      setNote(
        b.approved === b.asked
          ? `Approved ${b.approved}. They go out with the next send.`
          : `Approved ${b.approved} of ${b.asked}; the rest were no longer held.`,
      )
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="card" aria-labelledby="held-h" style={{ marginTop: 18, maxWidth: 860 }}>
        <h2 id="held-h" className="section-heading" style={{ marginTop: 0 }}>
          Held statements
        </h2>
        <p className="body-note" style={{ marginTop: 0 }}>
          A statement is held when its figures need a look first: a claim-volume check, payroll missing, no supervisor, or
          no email address. It is not emailed until approved here. Management with no team get no statement; they are
          on the review list below.
        </p>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', margin: '10px 0' }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="eyebrow">Month</span>
            <input
              type="month"
              value={month}
              onChange={(e) => {
                setRun(null)
                setMonth(e.target.value)
              }}
            />
          </label>
          {data && data.runs.length > 1 && (
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="eyebrow">Run</span>
              <select value={data.run ?? ''} onChange={(e) => setRun(e.target.value)}>
                {data.runs.map((r) => (
                  <option key={r.runId} value={r.runId}>
                    {r.mode === 'final' ? 'Final' : 'Dry run'}, {when(r.createdAt)} ({r.statements} statements, {r.held} held)
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {error && (
          <p className="body-note" role="alert" style={{ color: 'var(--crit-ink)' }}>
            {error}
          </p>
        )}
        {!data && !error && <span className="eyebrow">Loading…</span>}
        {data && !current && <p className="body-note">No statements have been made for {monthYear(month)} yet.</p>}
        {data && current && (
          <>
            <p className="body-note">
              {current.mode === 'final' ? 'Final run' : 'Dry run'} of {when(current.createdAt)} by {current.createdBy}:{' '}
              {current.statements} statements, {waiting.length} still held
              {approved.length ? `, ${approved.length} approved` : ''}.
              {current.mode === 'dry-run' && ' A dry run is a rehearsal; approving here does not touch the final run.'}
            </p>
            {waiting.length > 0 && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="dt" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            aria-label="Tick every held statement"
                            checked={ticked.size === waiting.length}
                            onChange={(e) => setTicked(e.target.checked ? new Set(waiting.map((s) => s.id)) : new Set())}
                          />
                        </th>
                        <th>Recipient</th>
                        <th>Statement</th>
                        <th>Why it is held</th>
                        <th style={{ textAlign: 'right' }}>Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waiting.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Approve ${s.employee}, ${KIND_LABEL[s.kind]}`}
                              checked={ticked.has(s.id)}
                              onChange={(e) => {
                                const next = new Set(ticked)
                                if (e.target.checked) next.add(s.id)
                                else next.delete(s.id)
                                setTicked(next)
                              }}
                            />
                          </td>
                          <td>{s.employee}</td>
                          <td>{KIND_LABEL[s.kind]}</td>
                          <td>{reasonText(s.holdReasons) || 'for review'}</td>
                          <td className="mono" style={{ textAlign: 'right' }}>
                            {s.id}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                  <button type="button" className="btn rules-primary" disabled={ticked.size === 0 || busy} onClick={approve}>
                    {busy
                      ? 'Approving…'
                      : ticked.size
                        ? `Approve ${ticked.size} ${ticked.size === 1 ? 'statement' : 'statements'}`
                        : 'Approve'}
                  </button>
                  <span className="body-note">Approved statements go out with the next send. This cannot be undone here.</span>
                </div>
              </>
            )}
            {note && (
              <p className="body-note" role="status" style={{ marginTop: 8 }}>
                {note}
              </p>
            )}
            {review === null ? (
              <p className="body-note" style={{ marginTop: 12 }}>
                The review list for this run could not be read.
              </p>
            ) : (
              review.length > 0 && (
                <>
                  <h3 className="eyebrow" style={{ marginTop: 18 }}>
                    Review list ({review.length})
                  </h3>
                  <p className="body-note" style={{ marginTop: 4 }}>
                    Everyone this run flagged. Management with no team get no statement this month, so there is nothing to
                    approve or send for them: check that is right.
                  </p>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="dt" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th>Person</th>
                          <th>Why</th>
                          <th>Statement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {review.map((p) => (
                          <tr key={p.employee}>
                            <td>{p.employee}</td>
                            <td>{reasonText(p.reasons)}</td>
                            <td>
                              {p.statement === 'held'
                                ? 'Held: approve above'
                                : p.statement === 'approved'
                                  ? 'Approved'
                                  : 'None: nothing is sent'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            )}
            {approved.length > 0 && (
              <details style={{ marginTop: 12 }}>
                <summary className="eyebrow" style={{ cursor: 'pointer' }}>
                  Approved ({approved.length})
                </summary>
                <div style={{ overflowX: 'auto' }}>
                  <table className="dt" style={{ width: '100%', marginTop: 6 }}>
                    <tbody>
                      {approved.map((s) => (
                        <tr key={s.id}>
                          <td>{s.employee}</td>
                          <td>{KIND_LABEL[s.kind]}</td>
                          <td>{reasonText(s.holdReasons)}</td>
                          <td>
                            {s.approvedBy}, {s.approvedAt ? when(s.approvedAt) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </>
        )}
      </section>
      {current && <Sends run={current} />}
    </>
  )
}

/* ------------------------------------------------------------------ sends */

interface SendStatement {
  id: number
  kind: 'employee' | 'team' | 'department'
  employee: string
  status: string
  sends: { to: string; outcome: 'sent' | 'failed'; guide: boolean; detail: string | null; by: string; at: string }[]
  opened: { first: string; last: string; opens: number; downloads: number } | null
}

type SendFilter = 'tried' | 'sent' | 'failed' | 'notSent' | 'opened' | 'all'

const FILTER_LABEL: Record<SendFilter, string> = {
  tried: 'Emailed or tried',
  sent: 'Sent',
  failed: 'Failed',
  notSent: 'Not yet sent',
  opened: 'Opened in the app',
  all: 'Every statement',
}

/**
 * Delivery for one run, read-only: each email the sender recorded (cpc.statement_send) and
 * whether the recipient opened the statement in the app (cpc.statement_link_log, their own
 * opens only). No email-open tracking, by design.
 */
function Sends({ run }: { run: Run }) {
  const api = useApi()
  const [rows, setRows] = useState<SendStatement[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<SendFilter>('tried')

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setError(null)
    api<{ statements: SendStatement[] }>(`sends?run=${run.runId}`)
      .then((b) => !cancelled && setRows(b.statements))
      .catch((e: Error) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [api, run.runId])

  const counts = rows ? sendsSummary(rows) : null
  const shown = (rows ?? []).filter((r) =>
    filter === 'all'
      ? true
      : filter === 'tried'
        ? r.sends.length > 0
        : filter === 'opened'
          ? !!r.opened
          : sendState(r) === filter,
  )

  return (
    <section className="card" aria-labelledby="sends-h" style={{ marginTop: 18, maxWidth: 860 }}>
      <h2 id="sends-h" className="section-heading" style={{ marginTop: 0 }}>
        Sends
      </h2>
      <p className="body-note" style={{ marginTop: 0 }}>
        Each email of this run, and whether the person has opened or downloaded their statement in the app. Bounces and
        replies arrive in the apps@fcc-corp.com mailbox. Whether an email was opened is not tracked, on purpose.
      </p>
      {run.mode === 'dry-run' && <p className="body-note">A dry run is never emailed, so nothing here is sent.</p>}
      {error && (
        <p className="body-note" role="alert" style={{ color: 'var(--crit-ink)' }}>
          {error}
        </p>
      )}
      {!rows && !error && <span className="eyebrow">Loading…</span>}
      {counts && (
        <>
          <p className="body-note">
            <strong>{counts.sent}</strong> sent, <strong>{counts.failed}</strong> failed,{' '}
            <strong>{counts.notSent}</strong> not yet sent; <strong>{counts.opened}</strong> opened in the app.
          </p>
          <label style={{ display: 'inline-grid', gap: 4, margin: '6px 0 10px' }}>
            <span className="eyebrow">Show</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value as SendFilter)}>
              {(Object.keys(FILTER_LABEL) as SendFilter[]).map((k) => (
                <option key={k} value={k}>
                  {FILTER_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          {shown.length === 0 ? (
            <p className="body-note">None.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="dt" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Statement</th>
                    <th>Email</th>
                    <th>Opened in the app</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const last = r.sends[0]
                    const state = sendState(r)
                    return (
                      <tr key={r.id}>
                        <td>{r.employee}</td>
                        <td>{KIND_LABEL[r.kind]}</td>
                        <td>
                          {last ? (
                            <>
                              <span style={state === 'failed' ? { color: 'var(--crit-ink)' } : undefined}>
                                {last.outcome === 'sent' ? 'Sent' : 'Failed'}
                              </span>{' '}
                              to {last.to}, {when(last.at)}
                              <div className="body-note" style={{ margin: 0 }}>
                                by {last.by}
                                {last.guide ? ', with the guide' : ''}
                                {r.sends.length > 1 ? `; ${r.sends.length} attempts` : ''}
                                {last.detail ? `: ${last.detail}` : ''}
                              </div>
                            </>
                          ) : r.status === 'held' ? (
                            'Held: not sent until approved'
                          ) : (
                            'Not yet sent'
                          )}
                        </td>
                        <td>
                          {r.opened ? (
                            <>
                              {when(r.opened.first)}
                              {(r.opened.opens > 1 || r.opened.downloads > 0) && (
                                <div className="body-note" style={{ margin: 0 }}>
                                  {r.opened.opens} {r.opened.opens === 1 ? 'open' : 'opens'}, {r.opened.downloads}{' '}
                                  {r.opened.downloads === 1 ? 'download' : 'downloads'}; last {when(r.opened.last)}
                                </div>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
